import React, {
  createContext,
  useContext,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import useWebSocket from 'react-use-websocket';
import { useDispatch, useSelector } from 'react-redux';
import { updateChat } from 'reduxToolkit/inboxSlice';
import { webSocketUrl } from '../api/ApiConfig';
import {
  flushWebChat,
  setConnectedUsersList,
  updateActiveUsers,
  updateMessageStatus,
} from '../reduxToolkit/inboxSlice';
// import { handleWappMessage } from '../reduxToolkit/whatsappInboxSlice';
import { setSocketConnection } from '../reduxToolkit/appSlice';

const SocketContext = createContext(null);

export const SocketProvider = ({ children }) => {
  const dispatch = useDispatch();
  const baseUrl = `${webSocketUrl}ws/system-user`;
  const { currentBrand, featureFlags } = useSelector((state) => state?.app);
  const token = localStorage.getItem('authToken');
  const brandId = currentBrand?.brand_id;

  // Track connection state to prevent multiple connections
  const connectionRef = useRef(null);
  const isConnectingRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;

  const socketUrl = useMemo(() => {
    if (!token || !brandId || !featureFlags?.webchat_feature) return null;
    return `${baseUrl}?brand_id=${brandId}&token=${token}`;
  }, [baseUrl, brandId, token, featureFlags]);

  const onMessage = useCallback(
    (event) => {
      try {
        // This arr holds the types of messages we want to process even though we are not on the webchat page or whatsapp page
        const allowed_types = [
          'connected_users_list',
          'user_connection_update',
        ];
        const origins = ['/user/webchat', '/user/new-inbox'];

        const messages = event.data.split('\n').filter((m) => m.trim() !== '');
        console.log('WebSocket messages received:', messages.length);

        messages.forEach((message) => {
          const content = JSON.parse(message);

          if (!content?.type) {
            console.error('type is missing');
            return;
          }

          // return when not in origins list and type is not in allowed types
          if (
            !origins.includes(window.location.pathname) &&
            !allowed_types.includes(data?.type)
          ) {
            console.log(
              'Ignoring message due to origin/type:',
              window.location.pathname,
              data?.type,
            );
            return;
          }
          const data = content?.content;

          try {
            console.log({ data });
            const service_type = content.type.split('.');
            if (service_type[0] === 'webchat') {
              switch (data?.type) {
                case 'chat':
                  if (!data?.current_message) {
                    console.error('current_message is missing');
                    return;
                  }
                  dispatch(updateChat(data));
                  localStorage.setItem(
                    'last_conversation_message_id',
                    data?.current_message?.id,
                  );
                  break;
                // case 'connected_users_list':
                //   dispatch(setConnectedUsersList(data));
                //   break;
                // case 'user_connection_update':
                //   dispatch(updateActiveUsers(content));
                //   break;
                case 'sent':
                case 'seen':
                case 'delivered':
                  dispatch(updateMessageStatus(content));
                  break;
                default:
                  console.warn('Unhandled message type:', data?.type);
                  break;
              }
            } else if (service_type[0] === 'whatsapp') {
              // console.log('whatsapp message received', data);
              // dispatch(handleWappMessage(content));
              console.log('whatsapp message received');
            } else if (content.type === 'connected_users_list') {
              dispatch(setConnectedUsersList(content));
            } else if (content.type === 'user_connection_update') {
              dispatch(updateActiveUsers(content));
            } else {
              console.warn('Unhandled service type:', service_type[0]);
            }
          } catch (parseError) {
            console.error(
              'Error parsing individual message:',
              parseError,
              message,
            );
          }
        });
      } catch (error) {
        console.error('Error processing WebSocket message:', error);
      }
    },
    [dispatch],
  );

  const onOpen = useCallback(() => {
    console.log('✅ WebSocket connected successfully');
    isConnectingRef.current = false;
    reconnectAttemptsRef.current = 0;
    dispatch(setSocketConnection(true));
  }, [dispatch]);

  const onClose = useCallback(
    (event) => {
      console.log('❌ WebSocket closed:', event.code, event.reason);
      isConnectingRef.current = false;
      dispatch(setSocketConnection(false));

      // Only log reconnection attempts for unexpected closures
      if (event.code !== 1000) {
        // 1000 = normal closure
        console.log(
          `Reconnection attempt ${
            reconnectAttemptsRef.current + 1
          }/${maxReconnectAttempts}`,
        );
      }
    },
    [dispatch],
  );

  const onError = useCallback((err) => {
    console.error('⚠️ WebSocket error:', err);
    isConnectingRef.current = false;
  }, []);

  const shouldReconnect = useCallback((closeEvent) => {
    // Don't reconnect if it was a normal closure or if we've exceeded max attempts
    if (closeEvent.code === 1000) {
      console.log('Normal closure, not reconnecting');
      return false;
    }

    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      console.log('Max reconnection attempts reached');
      return false;
    }

    reconnectAttemptsRef.current++;
    console.log(
      `Will attempt reconnection ${reconnectAttemptsRef.current}/${maxReconnectAttempts}`,
    );
    return true;
  }, []);

  const { sendMessage, lastMessage, readyState, getWebSocket } = useWebSocket(
    socketUrl,
    {
      share: false, // Important: Don't share connections
      shouldReconnect,
      reconnectInterval: (attemptNumber) => {
        // Exponential backoff: 1s, 2s, 4s, 8s, 16s
        return Math.min(1000 * Math.pow(2, attemptNumber), 30000);
      },
      onOpen,
      onClose,
      onError,
      onMessage,
    },
    socketUrl !== null, // Only connect if we have a valid URL
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const ws = getWebSocket();
      if (ws && ws.readyState === WebSocket.OPEN) {
        console.log('🧹 Cleaning up WebSocket connection on unmount');
        ws.close(1000, 'Component unmounted');
      }
    };
  }, [getWebSocket]);

  // Enhanced send functions with connection checks
  const sendMessageSafe = useCallback(
    (message) => {
      if (readyState === WebSocket.OPEN) {
        sendMessage(message);
      } else {
        console.warn('WebSocket not connected, message not sent:', message);
      }
    },
    [sendMessage, readyState],
  );

  const sendReadEvent = useCallback(
    (params) => {
      const {
        conversation_id,
        read_record_id,
        brand_id,
        team_id,
        channel_id,
        sender_id,
        sender_type,
        seen_message_id,
      } = params;

      // Validation
      const required = {
        conversation_id,
        seen_message_id,
        brand_id,
        team_id,
        sender_id,
      };
      const missing = Object.entries(required)
        .filter(([key, value]) => !value)
        .map(([key]) => key);

      if (missing.length > 0) {
        console.error('Missing required parameters:', missing);
        return;
      }

      const message = {
        type: 'read',
        brand_id: String(brand_id),
        team_id: String(team_id),
        conversation_id: Number(conversation_id),
        read_record_id: Number(read_record_id),
        sender_id: String(sender_id),
        sender_type: sender_type || 'system_user',
        seen_message_id: Number(seen_message_id),
      };

      if (channel_id) message.channel_id = String(channel_id);
      sendMessageSafe(JSON.stringify(message));
    },
    [sendMessageSafe],
  );

  const sendChatEvent = useCallback(
    (params) => {
      const {
        conversation_id,
        content,
        brand_id,
        sender_id,
        channel_id,
        sender_type,
        team_id,
      } = params;

      const required = {
        conversation_id,
        content,
        brand_id,
        team_id,
        sender_type,
        sender_id,
      };
      const missing = Object.entries(required)
        .filter(([key, value]) => !value)
        .map(([key]) => key);

      if (missing.length > 0) {
        console.error('Missing required parameters:', missing);
        return;
      }

      const message = {
        type: 'chat',
        content,
        timestamp: Date.now(),
        conversation_id: Number(conversation_id),
        brand_id: String(brand_id),
        team_id: String(team_id),
        sender_type: sender_type || 'system_user',
      };

      if (channel_id) message.channel_id = String(channel_id);
      if (sender_id) message.sender_id = String(sender_id);

      sendMessageSafe(JSON.stringify(message));
    },
    [sendMessageSafe],
  );

  const sendGetConnectionDetailsEvent = useCallback(
    (params) => {
      const { conversation_id, brand_id } = params;

      if (!conversation_id || !brand_id) {
        console.error('Missing required parameters: conversation_id, brand_id');
        return;
      }

      const message = {
        type: 'get_connection',
        conversation_id: Number(conversation_id),
        brand_id: String(brand_id),
      };

      sendMessageSafe(JSON.stringify(message));
    },
    [sendMessageSafe],
  );

  const contextValue = useMemo(
    () => ({
      sendMessage: sendMessageSafe,
      lastMessage,
      readyState,
      getWebSocket,
      sendChatEvent,
      sendGetConnectionDetailsEvent,
      sendReadEvent,
      isConnected: readyState === WebSocket.OPEN,
      connectionState: readyState,
    }),
    [
      sendMessageSafe,
      lastMessage,
      readyState,
      getWebSocket,
      sendChatEvent,
      sendGetConnectionDetailsEvent,
      sendReadEvent,
    ],
  );

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export default SocketProvider;
